<script setup>
import {computed, useSlots, ref, onMounted, onUnmounted, nextTick, watch} from "vue";
import IconCross from "@/assets/icons/IconCross.vue";
import InputSearchSecondary from "@/components/Form/InputSearchSecondary.vue";
import ButtonAccentPrimary from "@/components/Buttons/Default/ButtonAccentPrimary.vue";
import DropdownItem from "@/components/Dropdown/DropdownItem.vue";
import Chip from "@/components/Chips/Chip.vue";
import { useClickOutsideDropdown } from "@/components/Dropdown/useClickOutsideDropdown";

const props = defineProps({
  title: {
    type: String,
    default: ""
  },
  useCrossIcon: {
    type: Boolean,
    default: true
  },
  useConfirm: {
    type: Boolean,
    default: false
  },
  confirmButtonText: {
    type: String,
    default: "Кнопка"
  },
  items: {
    type: Array,
    required: true,
    default: () => []
  },
  modelValue: {
    type: [String, Number, Array],
    default: null
  },
  labelKey: {
    type: String,
    default: "label"
  },
  valueKey: {
    type: String,
    default: "value"
  },
  /**
   * Расположение dropdown относительно триггера
   *          bottom-left, bottom-center, bottom-right,
   *          top-left, top-center, top-right,
   *          left, right
   */
  position: {
    type: String,
    default: "bottom-left",
    validator: (value) => [
      "bottom-left", "bottom-center", "bottom-right",
      "top-left", "top-center", "top-right",
      "left", "right"
    ].includes(value)
  },
  isVisible: {
    type: Boolean,
    default: false
  },
  emptyMessage: {
    type: String,
    default: ""
  },
  teleport: {
    type: String,
    default: "#appMain"
  },
  useSearchbar: {
    type: Boolean,
    default: true
  },
  width: {
    type: [String, Number],
    default: null,
    validator: (value) => {
      if (value === null) return true;
      if (typeof value === "number") return value >= 0;
      return /^(auto|\d+(px|rem|em|%|vh|vw))$/.test(value);
    }
  },
  height: {
    type: [String, Number],
    default: null,
    validator: (value) => {
      if (value === null) return true;
      if (typeof value === "number") return value >= 0;
      return /^(auto|\d+(px|rem|em|%|vh|vw))$/.test(value);
    }
  },
  maxWidth: {
    type: [String, Number],
    default: null,
    validator: (value) => {
      if (value === null) return true;
      if (typeof value === "number") return value >= 0;
      return /^(auto|\d+(px|rem|em|%|vh|vw))$/.test(value);
    }
  },
  maxHeight: {
    type: [String, Number],
    default: null,
    validator: (value) => {
      if (value === null) return true;
      if (typeof value === "number") return value >= 0;
      return /^(auto|\d+(px|rem|em|%|vh|vw))$/.test(value);
    }
  },
  useShowSelected: {
    type: Boolean,
    default: false
  },
  triggerHeight: {
    type: [String, Number],
    default: null,
    validator: (value) => {
      if (value === null) return true;
      if (typeof value === "number") return value >= 0;
      return /^(auto|\d+(px|rem|em|%|vh|vw))$/.test(value);
    }
  },
  selectedListMaxHeight: {
    type: [String, Number],
    default: null,
    validator: (value) => {
      if (value === null) return true;
      if (typeof value === "number") return value >= 0;
      return /^(auto|\d+(px|rem|em|%|vh|vw))$/.test(value);
    }
  }
});

const emit = defineEmits(["update:modelValue", "item-select", "close", "confirm", "update:isVisible"]);

const computedSelectedListMaxHeight = computed(() => {
  if (!props.selectedListMaxHeight) return null;
  if (typeof props.selectedListMaxHeight === "number") {
    return `${props.selectedListMaxHeight}px`;
  }
  return props.selectedListMaxHeight;
});

const computedTriggerHeight = computed(() => {
  if (!props.triggerHeight) return null;
  if (typeof props.triggerHeight === "number") {
    return `${props.triggerHeight}px`;
  }
  return props.triggerHeight;
});

const computedMaxHeight = computed(() => {
  if (!props.maxHeight) return null;
  if (typeof props.maxHeight === "number") {
    return `${props.maxHeight}px`;
  }
  return props.maxHeight;
});

const computedMaxWidth = computed(() => {
  if (!props.maxWidth) return "315px";
  if (typeof props.maxWidth === "number") {
    return `${props.maxWidth}px`;
  }
  return props.maxWidth;
});

const computedHeight = computed(() => {
  if (!props.height) return null;
  if (typeof props.height === "number") {
    return `${props.height}px`;
  }
  return props.height;
});

const computedWidth = computed(() => {
  if (!props.width) return "315px";
  if (typeof props.width === "number") {
    return `${props.width}px`;
  }
  return props.width;
});

// Отслеживаем изменения isVisible для обновления позиции
watch(() => props.isVisible, (newVal) => {
  if (newVal) {
    nextTick(() => {
      // Добавляем небольшую задержку для корректного рендеринга dropdown
      updateDropdownPosition();
    });
  }
});

const slots = useSlots();

const dropdownRef = ref(null);
const triggerRef = ref(null);
const dropdownPosition = ref({ top: 0, left: 0 });

// Клик вне dropdown
useClickOutsideDropdown(triggerRef, () => {
  if (props.isVisible) {
    if (props.useConfirm) {
      handleClose();
    } else {
      emit("close");
    }
  }
});

// Рассчет позиции для dropdown (по аналогии с GlobusCalendar)
function updateDropdownPosition() {
  const t = triggerRef.value;
  if (!t) return;

  const rect = t.getBoundingClientRect();
  const scrollY = window.scrollY;
  const scrollX = window.scrollX;

  const offset = 10;
  const dropdownEl = dropdownRef.value;
  const dw = dropdownEl?.offsetWidth || 200;
  const dh = dropdownEl?.offsetHeight || 160;

  let top = 0;
  let left = 0;

  switch (props.position) {
    case "bottom-left":
      top = rect.bottom + scrollY + offset;
      left = rect.left + scrollX;
      break;
    case "bottom-center":
      top = rect.bottom + scrollY + offset;
      left = rect.left + scrollX + rect.width / 2 - dw / 2;
      break;
    case "bottom-right":
      top = rect.bottom + scrollY + offset;
      left = rect.right + scrollX - dw;
      break;
    case "top-left":
      top = rect.top + scrollY - dh - offset;
      left = rect.left + scrollX;
      break;
    case "top-center":
      top = rect.top + scrollY - dh - offset;
      left = rect.left + scrollX + rect.width / 2 - dw / 2;
      break;
    case "top-right":
      top = rect.top + scrollY - dh - offset;
      left = rect.right + scrollX - dw;
      break;
    case "left":
      top = rect.top + scrollY + rect.height / 2 - dh / 2;
      left = rect.left + scrollX - dw - offset;
      break;
    case "right":
      top = rect.top + scrollY + rect.height / 2 - dh / 2;
      left = rect.right + scrollX + offset;
      break;
    default:
      // По умолчанию нижний левый
      top = rect.bottom + scrollY + offset;
      left = rect.left + scrollX;
  }

  // Проверка границ viewport (учитываем скролл)
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const currentViewportTop = scrollY;
  const currentViewportBottom = scrollY + viewportHeight;
  
  // Горизонтальные границы
  if (left + dw > viewportWidth) {
    left = viewportWidth - dw - offset;
  }
  if (left < offset) {
    left = offset;
  }
  
  // Вертикальные границы - проверяем относительно видимой области
  if (top + dh > currentViewportBottom) {
    // Dropdown выходит за нижнюю границу - показываем сверху от trigger
    top = rect.top + scrollY - dh - offset;
  }
  if (top < currentViewportTop) {
    // Dropdown выходит за верхнюю границу - показываем снизу
    top = rect.bottom + scrollY + offset;
  }

  dropdownPosition.value = { top, left };
}

const handleResize = () => {
  updateDropdownPosition();
};

onMounted(() => {
  nextTick(() => {
    updateDropdownPosition();
  });
  window.addEventListener("resize", handleResize);
  window.addEventListener("scroll", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  window.removeEventListener("scroll", handleResize);
});

// Локальное состояние для режима с подтверждением
const tempSelectedItems = ref(null);

// Состояние для поиска
const searchQuery = ref("");

// Инициализируем локальное состояние
watch(() => props.modelValue, (newVal) => {
  if (props.useConfirm) {
    tempSelectedItems.value = Array.isArray(newVal) ? [...newVal] : newVal;
  }
}, { immediate: true });

const selectedItemsLocal = computed({
  set(value) {
    if (props.useConfirm) {
      // В режиме с подтверждением сохраняем локально
      tempSelectedItems.value = value;
    } else {
      // Без подтверждения - отправляем сразу
      emit("update:modelValue", value);
    }
  },
  get() {
    return props.useConfirm ? tempSelectedItems.value : props.modelValue;
  }
});

// Определяем тип выбора (одиночный или множественный)
const isMultipleSelection = computed(() => {
  return Array.isArray(props.modelValue);
});

const getItemValue = (item) => {
  if (typeof item === "object" && item !== null) {
    return item[props.valueKey] !== undefined ? item[props.valueKey] : item;
  }
  return item;
};

const getItemLabel = (item) => {
  if (typeof item === "object" && item !== null) {
    return item[props.labelKey] !== undefined ? item[props.labelKey] : String(item);
  }
  return String(item);
};

const handleItemChange = (item, isSelected) => {
  const itemValue = getItemValue(item);
  
  if (Array.isArray(selectedItemsLocal.value)) {
    // Множественный выбор
    const currentItems = [...selectedItemsLocal.value];
    if (isSelected) {
      if (!currentItems.some(selected => getItemValue(selected) === itemValue)) {
        currentItems.push(itemValue);
      }
    } else {
      const index = currentItems.findIndex(selected => getItemValue(selected) === itemValue);
      if (index > -1) {
        currentItems.splice(index, 1);
      }
    }
    selectedItemsLocal.value = currentItems;
  } else {
    // Одиночный выбор
    selectedItemsLocal.value = isSelected ? itemValue : null;
  }
  emit("item-select", { item, isSelected });
};

const handleConfirm = () => {
  // Отправляем локальные изменения в родитель
  emit("update:modelValue", tempSelectedItems.value);
  emit("confirm");
};

const handleClose = () => {
  // Сбрасываем локальные изменения до исходного состояния
  tempSelectedItems.value = Array.isArray(props.modelValue) ? [...props.modelValue] : props.modelValue;
  emit("close");
};

// Получение label для выбранного значения
const getSelectedItemLabel = (selectedValue) => {
  const foundItem = props.items.find(item => getItemValue(item) === selectedValue);
  return foundItem ? getItemLabel(foundItem) : String(selectedValue);
};

// Удаление выбранного элемента
const handleSelectedItemRemove = (selectedValue) => {
  const foundItem = props.items.find(item => getItemValue(item) === selectedValue);
  if (foundItem) {
    handleItemChange(foundItem, false);
  }
};

// Фильтрация элементов по поисковому запросу
const filteredItems = computed(() => {
  if (!searchQuery.value.trim()) {
    return props.items;
  }
  
  const query = searchQuery.value.toLowerCase().trim();
  return props.items.filter(item => {
    const label = getItemLabel(item).toLowerCase();
    return label.includes(query);
  });
});
</script>

<template>
  <div 
    ref="triggerRef"
    class="dropdown-trigger"
    :style="{'height': computedTriggerHeight}"
  >
    <slot name="trigger" />
    
    <Teleport :to="props.teleport">
      <div 
        v-if="isVisible"
        ref="dropdownRef"
        class="dropdown"
        :style="{ position: 'absolute', top: dropdownPosition.top + 'px', left: dropdownPosition.left + 'px', zIndex: 9999, width: computedWidth, height: computedHeight, 'max-height': computedMaxHeight, 'max-width': computedMaxWidth }"
      >
        <div
          v-if="props.title || slots['title-icon'] || props.useCrossIcon"
          class="dropdown-header"
        >
          <div
            v-if="props.title || slots['title-icon']"
            class="dropdown-header__left"
          >
            <div
              v-if="slots['title-icon']"
              class="dropdown-header__icon-user"
            >
              <slot name="title-icon" />
            </div>
            <span
              v-if="props.title"
              class="dropdown-header__title"
            >
              {{ props.title }}
            </span>
          </div>
          <IconCross
            v-if="props.useCrossIcon"
            class="dropdown-header__icon-cross"
            @click="props.useConfirm ? handleClose() : emit('close')"
          />
        </div>

        <div
          v-if="props.items.length > 0 && props.useSearchbar"
          class="dropdown__search"
        >
          <InputSearchSecondary 
            v-model="searchQuery"
            placeholder="Поиск" 
          />
        </div>

        <ul
          v-if="filteredItems.length > 0"
          class="dropdown__list scroll"
        >
          <li v-if="slots['dropdown-item']">
            <slot name="dropdown-item" />
          </li>
          <DropdownItem
            v-for="item in filteredItems"
            v-else
            :key="getItemValue(item)"
            :model-value="Array.isArray(selectedItemsLocal) 
              ? selectedItemsLocal.some(selected => 
                getItemValue(selected) === getItemValue(item)) 
              : getItemValue(selectedItemsLocal) === getItemValue(item)"
            class="dropdown__list-item"
            :label="getItemLabel(item)"
            :is-multiple="isMultipleSelection"
            @update:model-value="(value) => handleItemChange(item, value)"
          />
        </ul>

        <div
          v-else-if="props.items.length > 0 && filteredItems.length === 0"
          class="dropdown__list--empty"
        >
          {{ props.emptyMessage }}
        </div>

        <div
          v-else-if="props.items.length === 0"
          class="dropdown__list--empty"
        >
          {{ props.emptyMessage }}
        </div>
        <ul
          v-if="props.useShowSelected && selectedItemsLocal && selectedItemsLocal.length > 0"
          :style="`max-height: ${computedSelectedListMaxHeight}`"
          class="dropdown-selected__list scroll"
        >
          <li
            v-for="selectedValue in selectedItemsLocal"
            :key="selectedValue"
          >
            <Chip
              :label="getSelectedItemLabel(selectedValue)"
              :use-location-icon="false"
              class="dropdown-selected__item"
              @cancel="handleSelectedItemRemove(selectedValue)"
            />
          </li>
        </ul>
        <div
          v-if="slots['footer']"
          class="dropdown__footer"
        >
          <slot name="footer" />
        </div>
        <div
          v-else-if="!slots['footer'] && props.useConfirm"
          class="dropdown__footer"
        >
          <ButtonAccentPrimary
            v-if="props.useConfirm"
            size="large"
            @click="handleConfirm"
          >
            {{ props.confirmButtonText }}
          </ButtonAccentPrimary>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.dropdown-trigger {
  position: relative;
  display: inline-block;
}

.dropdown {
  position: absolute;
  z-index: 9999;
  background: var(--white);
  border-radius: 8px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 1px 2px 10px 0 var(--gray-gray50);
  width: 200px;
  //min-height: 120px;
  //max-height: 400px;

  hr {
    background-color: red;
    height: 1px;
  }


  &-selected__list {
    display: flex;
    gap: 5px;
    border-top: 1px solid var(--border-bd-divider);
    list-style: none;
    margin: 0;
    padding: 10px 0 0 0;
    min-height: 0;
    flex-wrap: wrap;
    flex-shrink: 0;
  }

  &__list {
    list-style: none;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 0 2px 0 0;

    &--empty {
      display: flex;
      justify-content: center;
      align-items: center;
      text-align: center;
      border-radius: 10px;
      padding: 20px;
      background: var(--background-01);
      font-size: 14px;
      font-weight: 400;
      line-height: 140%;

    }
  }

  &__footer {
    display: flex;
    button { flex-grow: 1; }
  }

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__left {
      display: flex;
      align-items: center;
      gap: 6px;
      flex: 1;
      min-width: 0;
      overflow: hidden;
    }


    &__icon {
      &-user {
        display: flex;
        justify-content: center;
        align-items: center;
        color: var(--text-text-pur);
      }

      &-cross {
        cursor: pointer;
        user-select: none;
        color: var(--text-text-pur);
        width: 16px;
        height: 16px;

        &:hover {
          color: var(--text-text-on);
        }
      }
    }

    &__title {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      font-weight: 500;
      font-size: 14px;
      color: var(--text-text-pur);
      flex: 1;
      min-width: 0;

    }
  }


}

// Стили для скрола
.scroll {
  overflow-y: auto;

  @-moz-document url-prefix() {
    scrollbar-width: thin;
    scrollbar-color: #1A64D433 transparent;
  }

  &::-webkit-scrollbar {
    width: 7px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #1A64D433;
    border-radius: 30px;
  }
}
</style>